import type { IconNode } from 'lucide-react'
import { observable, runInAction } from 'mobx'

type ShardModule<Name extends string> = {
  default: Partial<Record<Name, IconNode>>
}

/*
 * Icon art that loads by shard on first read, never on the startup path, so a library can grow
 * without the app growing. Reads stay synchronous: `read` answers undefined until a name's shard
 * lands, and whatever read it inside an observer (a component, a reaction, a `when`) runs again
 * once it does. A failed load is retried after a backoff by everything still waiting on it.
 */
export function createShardedArt<Name extends string, Shard extends string>({
  names,
  shardOf,
  loaders,
  missing,
}: {
  names: readonly Name[]
  shardOf: (name: Name) => Shard
  loaders: Record<Shard, () => Promise<ShardModule<Name>>>
  // What a name its shard doesn't carry is drawn with.
  missing: IconNode
}) {
  // Shallow: the generated arrays are stored as they are, never turned into observable proxies.
  const art = observable.map<Name, IconNode>(undefined, { deep: false })
  const retryEpoch = observable.box(0)
  const RETRY_DELAYS_MS = [1_000, 4_000, 16_000, 60_000]

  const loads = new Map<Shard, Promise<void>>()
  const pending = new Set<Shard>()
  const failures = new Map<Shard, number>()

  function loadShard(shard: Shard): Promise<void> {
    const known = loads.get(shard)
    if (known) return known

    pending.add(shard)
    const load = loaders[shard]()
      .then(({ default: shardArt }) => {
        failures.delete(shard)
        runInAction(() =>
          names
            .filter(name => shardOf(name) === shard)
            .forEach(name => art.set(name, shardArt[name] ?? missing)),
        )
      })
      .catch(() => {
        // The settled load stays in `loads` until its backoff ends, so reads in between start
        // nothing; after the last attempt it stays for good.
        const attempt = failures.get(shard) ?? 0
        failures.set(shard, attempt + 1)
        if (attempt < RETRY_DELAYS_MS.length) {
          setTimeout(() => {
            loads.delete(shard)
            runInAction(() => retryEpoch.set(retryEpoch.get() + 1))
          }, RETRY_DELAYS_MS[attempt])
        }
      })
      .finally(() => pending.delete(shard))
    loads.set(shard, load)
    return load
  }

  // Retries end after the last backoff; back online, a shard that gave up starts afresh.
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      const exhausted = [...failures.keys()].filter(
        shard => failures.get(shard) > RETRY_DELAYS_MS.length,
      )
      exhausted.forEach(shard => {
        failures.delete(shard)
        loads.delete(shard)
      })
      if (exhausted.length) {
        runInAction(() => retryEpoch.set(retryEpoch.get() + 1))
      }
    })
  }

  function request(name: Name): void {
    retryEpoch.get()
    void loadShard(shardOf(name))
  }

  return {
    /** A name's art, starting its load; undefined until it lands. */
    read(name: Name): IconNode | undefined {
      const nodes = art.get(name)
      if (!nodes) request(name)
      return nodes
    },
    /** A name's art if it has landed, without starting a load. */
    peek: (name: Name): IconNode | undefined => art.get(name),
    isLoaded(name: Name): boolean {
      if (art.has(name)) return true
      request(name)
      return false
    },
    hasPending: (): boolean => pending.size > 0,
    /** Settles once every load already started has. */
    settled: (): Promise<void> =>
      Promise.all([...pending].map(shard => loads.get(shard))).then(
        () => undefined,
      ),
  }
}
